import React, { useState, useEffect } from 'react';
import { Phone, Shield, Clock, MapPin, ArrowRight, AlertTriangle, Star, Zap, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import InteractiveDiagnostic from '../components/InteractiveDiagnostic';
import InterventionMap from '../components/InterventionMap';
import DynamicTestimonials from '../components/DynamicTestimonials';
import GamifiedContactForm from '../components/GamifiedContactForm';
import FloatingContactScript from '../components/FloatingContactScript';

const RevolutionaryHome = () => {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [visitorCount, setVisitorCount] = useState(0);

  useEffect(() => {
    // Simulate page load
    const timer = setTimeout(() => setHeroLoaded(true), 500);
    
    // Simulate visitor counter
    setVisitorCount(127 + Math.floor(Math.random() * 20));

    return () => clearTimeout(timer);
  }, []);

  const handleCall = () => {
    window.location.href = 'tel:0676583038';
  };

  const scrollToContact = () => {
    document.getElementById('gamified-contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToDiagnostic = () => {
    document.getElementById('interactive-diagnostic')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Revolutionary Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          {/* Geometric patterns */}
          <div className="absolute top-20 left-20 w-64 h-64 border border-yellow-400/20 rounded-full animate-pulse"></div>
          <div className="absolute bottom-32 right-32 w-48 h-48 border border-yellow-400/30 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-10 w-32 h-32 border border-yellow-400/10 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          
          {/* Floating particles */}
          {[...Array(15)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-yellow-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`
              }}
            />
          ))}

          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-600/5 via-transparent to-red-600/5"></div>
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
            
            {/* Hero Content */}
            <div className={`space-y-8 transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              {/* Urgent Alert */}
              <div className="flex items-center space-x-3">
                <div className="inline-flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-full font-semibold text-sm shadow-lg">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full animate-ping"></div>
                  <span>🚨 ALERTE FRELON ASIATIQUE</span>
                </div>
                <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold">
                  <Clock className="h-4 w-4" />
                  <span>Urgence 24h/24</span>
                </div>
              </div>

              {/* Main Headline */}
              <div className="space-y-6">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="block text-white">FRELONS ?</span>
                  <span className="block text-yellow-400 animate-pulse">NEUTRALISÉS</span>
                  <span className="block text-white">EN - DE 2H</span>
                </h1>
                
                <div className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                  <div className="mb-4">🎯 <strong className="text-white">L'expert anti-nuisibles nouvelle génération</strong></div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span>IA Diagnostic</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                      <span>Géoloc temps réel</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                      <span>Intervention 2.0</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                      <span>Garantie 100%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-6">
                <Button
                  onClick={handleCall}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-6 text-xl rounded-lg shadow-2xl transform hover:scale-110 transition-all duration-300 animate-pulse"
                >
                  <Phone className="h-6 w-6 mr-3" />
                  🚨 URGENCE : 06 76 58 30 38
                </Button>
                
                <Button
                  onClick={scrollToDiagnostic}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black font-bold px-10 py-6 text-xl rounded-lg shadow-2xl transform hover:scale-110 transition-all duration-300"
                >
                  <Target className="h-6 w-6 mr-3" />
                  🎯 DIAGNOSTIC IA GRATUIT
                </Button>
              </div>

              {/* Trust Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8">
                <Card className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-0 text-center">
                    <div className="text-2xl font-bold text-green-400">{visitorCount}</div>
                    <div className="text-sm text-gray-300">Visiteurs aujourd'hui</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-0 text-center">
                    <div className="text-2xl font-bold text-blue-400">{'< 2h'}</div>
                    <div className="text-sm text-gray-300">Temps intervention</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-0 text-center">
                    <div className="text-2xl font-bold text-yellow-400">4.9★</div>
                    <div className="text-sm text-gray-300">Note Google</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-0 text-center">
                    <div className="text-2xl font-bold text-red-400">24/7</div>
                    <div className="text-sm text-gray-300">Disponibilité</div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Hero Visual */}
            <div className={`relative transition-all duration-1000 delay-300 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <div className="relative z-10">
                <div className="transform hover:rotate-2 transition-all duration-500 hover:shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_frelon33-experts/artifacts/xehwbzjb_pulv%C3%A9riser.png"
                    alt="Intervention futuriste Frelon33" 
                    className="w-full h-auto rounded-2xl shadow-2xl"
                  />
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-full shadow-2xl animate-bounce">
                  <AlertTriangle className="h-8 w-8" />
                </div>

                <div className="absolute -bottom-8 -left-8 bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl shadow-2xl">
                  <div className="text-center">
                    <div className="text-2xl font-bold">98%</div>
                    <div className="text-sm">Succès</div>
                  </div>
                </div>

                <div className="absolute top-1/2 -right-12 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black p-4 rounded-full shadow-2xl animate-pulse">
                  <Zap className="h-6 w-6" />
                </div>
              </div>

              {/* Background Glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/30 to-red-400/30 rounded-2xl blur-3xl -z-10 transform scale-110"></div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="animate-bounce">
            <ArrowRight className="h-6 w-6 text-yellow-400 transform rotate-90" />
          </div>
        </div>
      </section>

      {/* Interactive Diagnostic Section */}
      <section id="interactive-diagnostic" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 transition-all duration-1000 opacity-100 translate-y-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              🤖 Diagnostic <span className="text-yellow-600">Intelligent</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Notre IA analyse votre situation en 30 secondes et vous donne 
              le niveau de danger exact avec plan d'action personnalisé
            </p>
          </div>
          
          <InteractiveDiagnostic />
        </div>
      </section>

      {/* Live Interventions Map */}
      <section className="py-20 bg-gradient-to-br from-black to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 transition-all duration-1000 opacity-100 translate-y-0">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              🗺️ Interventions <span className="text-yellow-400">en Direct</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Suivez nos interventions en temps réel sur toute la Gironde
            </p>
          </div>
          
          <InterventionMap />
        </div>
      </section>

      {/* Dynamic Testimonials */}
      <section className="py-20 bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="transition-all duration-1000 opacity-100 translate-y-0">
            <DynamicTestimonials />
          </div>
        </div>
      </section>

      {/* Gamified Contact Form */}
      <section id="gamified-contact-form" className="py-20 bg-gradient-to-br from-gray-100 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 transition-all duration-1000 opacity-100 translate-y-0">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              🎮 Formulaire <span className="text-yellow-600">Gamifié</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Remplissez votre demande étape par étape et débloquez votre devis gratuit !
            </p>
          </div>
          
          <GamifiedContactForm />
        </div>
      </section>

      {/* Final Revolutionary CTA */}
      <section className="py-20 bg-gradient-to-r from-black via-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-yellow-400 rounded-full animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8 transition-all duration-1000 opacity-100 translate-y-0">
            <div className="text-6xl mb-6">🚀</div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Prêt pour l'<span className="text-yellow-400">intervention</span> du futur ?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Rejoignez les 850+ clients qui ont choisi la nouvelle génération 
              d'experts anti-nuisibles. <strong>Résultats garantis ou remboursé.</strong>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                onClick={handleCall}
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-12 py-6 text-2xl rounded-lg shadow-2xl animate-pulse transform hover:scale-105 transition-all duration-300"
              >
                <Phone className="h-8 w-8 mr-3" />
                🚨 06 76 58 30 38
              </Button>
              
              <Button
                onClick={scrollToDiagnostic}
                className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-black font-bold px-12 py-6 text-2xl rounded-lg shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <Target className="h-8 w-8 mr-3" />
                DIAGNOSTIC GRATUIT
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 pt-12">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Shield className="h-6 w-6 text-green-400" />
                <span className="font-semibold">Certifié Ministère</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Clock className="h-6 w-6 text-blue-400" />
                <span className="font-semibold">Intervention {'< 2h'}</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Star className="h-6 w-6 text-yellow-400" />
                <span className="font-semibold">4.9★ Google</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Contact Script */}
      <FloatingContactScript />
    </div>
  );
};

export default RevolutionaryHome;